/**
 * AdminIntereses Page
 * 
 * Admin page for managing presential course interest records.
 * Requires admin authentication.
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, 
  Shield, 
  Users,
  Loader2,
  AlertCircle
} from 'lucide-react';
import InterestDashboard from '../components/InterestDashboard';
import { getStoredToken, storeToken, clearToken } from '../services/courseInterestApi';

/**
 * Payment Service base URL from environment variable
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * AdminIntereses Page Component
 */
export default function AdminIntereses() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  /**
   * Check authentication on mount
   */
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      // Verify token is still valid by making a test request
      verifyToken(token);
    } else {
      setCheckingAuth(false);
    }
  }, []);

  /**
   * Verify token is still valid
   */
  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${PAYMENT_API_URL}/api/admin/course-interest?limit=1`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
        setAdmin({ email: 'admin' }); // We don't store admin details
      } else {
        clearToken();
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      clearToken();
    } finally {
      setCheckingAuth(false);
    }
  };

  /**
   * Handle login
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    try {
      const response = await fetch(`${PAYMENT_API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Credenciales inválidas');
      }

      if (data.success && data.token) {
        storeToken(data.token);
        setIsAuthenticated(true);
        setAdmin(data.user || { email });
      }
    } catch (err) {
      setLoginError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoginLoading(false);
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    clearToken();
    setIsAuthenticated(false);
    setAdmin(null);
    setEmail('');
    setPassword('');
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Background decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo and title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">
                    Gestión de Intereses
                  </h1>
                  <p className="text-xs text-white/50">
                    Instituto Valparaíso - Admin
                  </p>
                </div>
              </div>

              {/* User info and logout */}
              {isAuthenticated && admin && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-white/70 hidden sm:block">
                    {admin.email}
                  </span>
                  <motion.button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm
                               text-white/70 hover:text-white
                               bg-white/5 hover:bg-white/10 rounded-lg
                               border border-white/10 hover:border-white/20
                               transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Cerrar Sesión</span>
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AnimatePresence mode="wait">
            {/* Login Screen */}
            {!isAuthenticated && (
              <motion.div
                key="login"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center justify-center min-h-[calc(100vh-12rem)]"
              >
                <div className="w-full max-w-md">
                  <div className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                    {/* Header */}
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-white mb-2">
                        Acceso Administrador
                      </h2>
                      <p className="text-white/60 text-sm">
                        Ingresa tus credenciales para gestionar los intereses en cursos presenciales
                      </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className="block text-sm text-white/60 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="admin@institutovalparaiso.cl"
                          required
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl
                                     text-white placeholder-white/30 text-sm
                                     focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-white/60 mb-2">
                          Contraseña
                        </label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          required
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl
                                     text-white placeholder-white/30 text-sm
                                     focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>

                      {/* Error Message */}
                      {loginError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl flex items-center gap-2"
                        >
                          <AlertCircle className="w-4 h-4 text-red-400" />
                          <span className="text-red-400 text-sm">{loginError}</span>
                        </motion.div>
                      )}

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        disabled={loginLoading}
                        whileHover={{ scale: loginLoading ? 1 : 1.02 }}
                        whileTap={{ scale: loginLoading ? 1 : 0.98 }}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500
                                   hover:from-blue-600 hover:to-cyan-600
                                   text-white font-semibold rounded-xl
                                   flex items-center justify-center gap-2
                                   disabled:opacity-50 disabled:cursor-not-allowed
                                   shadow-lg shadow-blue-500/30 transition-all"
                      >
                        {loginLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Iniciando sesión...
                          </>
                        ) : (
                          <>
                            <Shield className="w-5 h-5" />
                            Iniciar Sesión
                          </>
                        )}
                      </motion.button>
                    </form>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Authenticated - Dashboard */}
            {isAuthenticated && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <InterestDashboard />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <p className="text-center text-sm text-white/40">
              © {new Date().getFullYear()} Instituto Valparaíso. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
